import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { supabase } from '@/lib'
import { useTranslation } from 'react-i18next'
import { useUiStore } from '@/stores/uiStore'

const schema = z.object({
  email: z.string().email('Invalid email'),
})

type FormData = z.infer<typeof schema>

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [serverMsg, setServerMsg] = useState('')
  const [sent, setSent] = useState(false)

  useEffect(() => {
    document.documentElement.classList.add('dark')
    return () => {
      const { theme } = useUiStore.getState()
      if (theme === 'light') {
        document.documentElement.classList.remove('dark')
      }
    }
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  async function onSubmit(data: FormData) {
    setServerMsg('')
    const redirectTo = `${window.location.origin}/auth/update-password`
    const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
      redirectTo,
    })
    if (error) {
      setServerMsg(error.message)
      return
    }
    setSent(true)
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#060608] text-slate-100 flex items-center justify-center p-4 font-body antialiased selection:bg-purple-500/30">
      <div
        className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.20) 0%, transparent 65%)', filter: 'blur(40px)' }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(76,215,246,0.10) 0%, transparent 65%)', filter: 'blur(40px)' }}
        aria-hidden
      />

      <Link
        to="/"
        className="absolute top-6 left-6 z-20 flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white/70 backdrop-blur-md transition-all hover:bg-white/10 hover:text-white sm:top-8 sm:left-8"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('auth.backToHome')}
      </Link>

      <div
        className="group relative z-10 rounded-[28px] border border-white/[0.06] bg-[#0c0c11] overflow-hidden transition-all duration-500 hover:border-white/[0.12] hover:bg-[#101016] w-full max-w-md p-8"
        style={{
          boxShadow: '0 10px 40px -10px rgba(0,0,0,0.5)',
        }}
      >
        <div
          className="pointer-events-none absolute w-[150%] h-[150%] rounded-full blur-[80px] transition-opacity duration-700 opacity-60 group-hover:opacity-100 top-[20%] left-[20%]"
          style={{
            background: `radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 60%)`,
          }}
          aria-hidden
        />

        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.8'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
          }}
          aria-hidden
        />

        <div className="relative z-10 flex flex-col h-full">
          <div className="mb-8 flex justify-center">
            <img src="/logo_light_mode.png" alt="Skills Up" className="h-10 w-auto object-contain dark:hidden" />
            <img src="/skillsuplogo.png" alt="Skills Up" className="hidden h-10 w-auto object-contain dark:block" />
          </div>

          <h2 className="font-headline text-2xl font-bold text-white text-center">{t('auth.forgotPassword.title')}</h2>
          <p className="mb-6 mt-2 text-sm text-white/60 text-center">
            {t('auth.forgotPassword.subtitle')}
          </p>

          {sent ? (
            <p className="text-sm text-emerald-200 bg-emerald-500/10 border border-emerald-400/30 rounded-lg px-4 py-3">
              {t('auth.forgotPassword.successMessage')}
            </p>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-1">{t('auth.forgotPassword.emailLabel')}</label>
                <input
                  {...register('email')}
                  type="email"
                  autoComplete="email"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#7c3aed] focus:outline-none focus:ring-1 focus:ring-[#7c3aed] transition-colors"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
                )}
              </div>
              {serverMsg && (
                <p className="text-red-500 text-xs">{serverMsg}</p>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-xl bg-[#7c3aed] py-3 text-sm font-headline font-bold uppercase tracking-wider text-white transition-all hover:bg-[#6d28d9] hover:shadow-[0_0_24px_rgba(124,58,237,0.4)] disabled:opacity-50 active:scale-[0.98]"
              >
                {isSubmitting ? t('auth.forgotPassword.submitting') : t('auth.forgotPassword.submitButton')}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-white/60">
            <Link to="/login" className="font-medium text-[#a78bfa] transition-colors hover:text-[#d8b4fe]">
              {t('auth.forgotPassword.backToSignIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
